<template>
  <div class="public-link">
    <an-toggle class="toggle-wrapper" v-model="enablePublicLink" @change="togglePublicLink">
      <div class="header">Publish</div>
      <div class="secondary-text">Turn your project into a live website</div>
    </an-toggle>
    <an-tooltip
      text="Publish to enable these features"
      position="bottom-right"
      offset="270"
      :disabled="enablePublicLink"
    >
      <div :class="[{ 'public-link-disabled': !enablePublicLink }]">
        <div class="public-link-container flex" :class="[{ 'disabled-area': !enablePublicLink }]">
          <div class="anima-subdomain-container">
            <div>
              <div>Use a free Anima subdomain</div>
              <div class="secondary-text">Connect a customized domain for free</div>
            </div>
            <div class="full-width">
              <div class="anima-subdomain-input-container">
                <an-input
                  class="anima-subdomain-input"
                  v-model="subdomain"
                  :invalid="!isValidSubDomain"
                  placeholder="my-subdomain"
                />

                <an-link
                  class="anima-subdomain-link"
                  @click="clickOnAnimaSubdomain"
                  :isWorking="isWorking"
                  variant="primary"
                  >{{ project.subdomain != subdomain ? 'Save' : 'Copy link' }}</an-link
                >
              </div>
            </div>
            <span class="secondary-text floating-text">.animaapp.io</span>
            <div class="error-message" v-if="subdomainError">{{ subdomainError }}</div>
          </div>
          <SettingsMeta hide-additional-settings :hide-custom-domain-list="!enablePublicLink" />
        </div>
      </div>
    </an-tooltip>

    <div class="flex" v-if="enablePublicLink">
      <an-toggle class="toggle-wrapper" v-model="enableHotspots" @change="toggleHotspots">
        <div class="header">Enable hotspots</div>
        <div class="secondary-text">Highlight clickable areas in prototype</div>
      </an-toggle>
      <an-toggle class="toggle-wrapper" v-model="enableRestartButton" @change="toggleRestartButton">
        <div class="header">Show restart button</div>
        <div class="secondary-text">Display button which navigates to homepage of prototype</div>
      </an-toggle>
      <an-toggle class="toggle-wrapper" v-model="enableInterface" @change="toggleInterface">
        <div class="header">Show navigation buttons</div>
        <div class="secondary-text">Show buttons that help navigate between screens</div>
      </an-toggle>
      <div class="full-width">
        <an-toggle class="toggle-wrapper" v-model="enablePassword" @change="togglePassword">
          <div class="header">Password protection</div>
          <div class="secondary-text">Protect the public link with an auto-generated password</div>
        </an-toggle>
        <div class="basic-auth" v-if="enablePassword">
          <CopyText v-if="enablePublicLink" label="Username" :text="username" @copied="toastCopied" />
          <CopyText v-if="enablePublicLink" label="Password" :text="password" @copied="toastCopied" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import CopyText from '@/components/CopyText/CopyText';
import copy from '@/utils/copyToClp';
import SettingsMeta from '@/components/Project/Settings/Meta';
import { getPrototypeUrl } from '@/utils/urls';
import { uuid } from '@/utils/uuid';
import { toastError, toastSuccess } from '@/services/bus';
import errorHandler from '@/services/errorHandler';
import { isEmpty } from 'lodash-es';
import { validateDomain } from '@/utils/urls';
export default {
  data() {
    return {
      enablePublicLink: false,
      enableHotspots: false,
      enablePassword: false,
      enableRestartButton: false,
      enableInterface: false,
      username: 'viewer',
      password: null,
      subdomain: null,
      subdomainError: null,
      isWorking: false
    };
  },
  components: {
    SettingsMeta,
    CopyText
  },
  computed: {
    ...mapState('projects', { project: 'currentItem' }),
    ...mapState('components', { screens: 'items' }),
    ...mapState('userOnboardings', { userOnboarding: 'currentItem' }),
    ...mapState('domains', { domains: 'items' }),
    ...mapGetters({
      projectSlugs: 'omniview/projectSlugs'
    }),
    toggleText() {
      const { is_public: isPublic } = this.project;
      return isPublic ? 'Disable link' : 'Enable link';
    },
    url() {
      const { enableRestartButton, enableInterface } = this;
      const { subdomain, short_id } = this.project;

      let path = '';

      let queryParams = {};
      if (enableRestartButton) {
        queryParams['r'] = 1;
      }
      if (enableInterface) {
        queryParams = {
          ...queryParams,
          p: short_id,
          h: this.homepageSlug,
          pages: btoa(JSON.stringify(this.projectSlugs))
        };
        path = this.homepageSlug;
      }
      return getPrototypeUrl({ subdomain, params: queryParams, path });
    },
    homepageSlug() {
      const { screenSlug } = this.$route.params;
      const { live_project_release_homepage_slug: homepageSlug } = this.project;
      const correctHomepageSlug = this.screens.some((screen) => screen.slug === homepageSlug);
      return correctHomepageSlug ? homepageSlug : screenSlug ? screenSlug : this.screens[0]?.slug;
    }
  },
  created() {
    const { query } = this.$route;
    const { is_public: isPublic } = this.project;
    this.subdomain = this.project.subdomain;
    if (query.tab === 'public-link' && !isPublic) {
      this.enablePublicLink = true;
      this.togglePublicLink(this.enablePublicLink);
    }
  },
  methods: {
    ...mapActions({
      updateProject: 'projects/update',
      reloadProject: 'projects/fetchOne',
      fetchScreens: 'components/fetchAllOfParent',
      nextOnboardingStage: 'userOnboardings/nextStage',
      fetchCustomDomains: 'domains/fetchAllOfParent'
    }),
    clickOnAnimaSubdomain() {
      if (this.project.subdomain != this.subdomain) {
        this.saveSubdomain();
      } else {
        toastSuccess('Link copied to clipboard.');
        copy('https://' + this.project.subdomain + '.animaapp.io');
      }
    },
    async saveSubdomain() {
      try {
        this.isWorking = true;
        this.subdomainError = '';

        this.$trackEvent('project-settings-public-link.save-button.click');

        if (!this.isValidSubDomain()) {
          if (!this.subdomain) {
            this.subdomainError = 'Subdomain is mandatory';
          } else {
            this.subdomainError = 'Subdomains can only consist of letters, numbers, and dashes.';
          }
          return;
        }

        await this.modifyProject({
          subdomain: this.subdomain
        });

        toastSuccess('Project updated successfully');
        this.$trackEvent('project-settings-public-link.save.success');
      } catch (err) {
        this.$trackEvent('project-settings-public-link.save.failure', { message: err.message });
        if (err.response?.data?.message === 'Subdomain is taken') {
          this.subdomainError = 'This subdomain is already taken 🙊';
        } else {
          toastError('Failed updating project');
        }
      } finally {
        this.isWorking = false;
      }
    },
    fetchScreensList() {
      const { projectId } = this.$route.params;
      this.fetchScreens({ parent: 'projects', id: projectId, params: { get_all: true, order_by: '-updated_at' } });
    },
    isValidSubDomain() {
      return validateDomain(`${this.subdomain}.animaapp.io`);
    },
    reset() {
      this.fetchScreensList();
      //get custom domains also when directly accessing the project settings
      if (this.domains.length === 0) {
        const { short_id: id } = this.project;
        this.fetchCustomDomains({
          parent: 'projects',
          id
        });
      }
      this.enablePublicLink = this.project.is_public;
      this.enableHotspots = this.project.is_hotspots_enabled;
      this.enablePassword = this.project.is_password_protected;
      this.enableRestartButton = this.project.is_restart_button_enabled;
      this.enableInterface = this.project.is_anima_interface_enabled;
      this.password = this.project.basic_auth_password;
      this.username = this.project.basic_auth_username;
      this.subdomain = this.project.subdomain;
    },
    generateRandomPassword() {
      return uuid({ withPrefix: false, length: 8 });
    },
    toggleHotspots(is_hotspots_enabled) {
      this.modifyProject({ is_hotspots_enabled });
    },
    toggleRestartButton(is_restart_button_enabled) {
      this.modifyProject({ is_restart_button_enabled });
    },
    toggleInterface(is_anima_interface_enabled) {
      this.modifyProject({ is_anima_interface_enabled });
    },
    async togglePassword(is_password_protected) {
      const basic_auth_username = is_password_protected ? 'viewer' : null;
      const basic_auth_password = is_password_protected ? this.generateRandomPassword() : null;
      this.username = basic_auth_username;
      this.password = basic_auth_password;
      await this.modifyProject({
        is_password_protected,
        basic_auth_username,
        basic_auth_password
      });
    },
    togglePublicLink(value) {
      this.enableHotspots = value;
      this.enableRestartButton = value;
      const payload = {
        is_public: value,
        is_hotspots_enabled: this.enableHotspots,
        is_restart_button_enabled: this.enableRestartButton,
        is_anima_interface_enabled: this.enableInterface
      };
      this.$trackEvent('project-invite.public-link.toggle', payload);
      this.modifyProject(payload);
    },
    async modifyProject(payload) {
      const { short_id: projectId } = this.project;
      try {
        await this.updateProject({ id: projectId, payload });
        this.reloadProject({ id: projectId, skipCache: true });
      } catch (err) {
        errorHandler.captureException(err);
        throw err;
      }
    },
    onCopy() {
      if (!isEmpty(this.userOnboarding)) {
        this.nextOnboardingStage({ currentStageSlug: 'prototype-link' });
      }
      this.$trackEvent('project-invite.public-link.copy');
      toastSuccess('Link copied to clipboard.');
    },
    toastCopied() {
      toastSuccess('Copied!');
    }
  },
  watch: {
    project: {
      handler: 'reset',
      immediate: true
    }
  }
};
</script>
<style lang="scss" scoped>
@import '@/styles/_fullscreenLayout.scss';
@import '@/styles/_publicLink.scss';
</style>
